import { Fragment } from 'react';

import { EllipsisItem } from '@/core/tamagoshiTailwind/components/Pagination/components/EllipsisItem';
import {
  PaginationItem,
  PaginationPageItem,
} from '@/core/tamagoshiTailwind/components/Pagination/components/PaginationItem';
import { calculatePaginationPages } from '@/core/tamagoshiTailwind/components/Pagination/utils';
import { ChevronLeftIcon } from '@/core/tamagoshiTailwind/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/core/tamagoshiTailwind/icons/ChevronRightIcon';

type PaginationProps = {
  /** The total number of pages  */
  count: number;
  /** The current page number, 1-based index */
  currentPage: number;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Extra page numbers to display, hardcoded */
  levels?: number[];
  /** The aria-label for the next page button */
  nextPageAriaLabel: string;
  /** The aria-label for the previous page button */
  previousPageAriaLabel: string;
} & (
  | {
      /** A function that must return the href for a given page */
      hrefForPage?: never;
      /** Callback for when the page changes. */
      onPageChange(page: number): void;
    }
  | {
      hrefForPage(page: number): string;
      onPageChange?(page: number): void;
    }
);

/**
 * Render a pagination component with a list of pages and navigation buttons.
 * It can be configured to render link components or buttons depending on
 * whether the hrefForPage prop is provided.
 *
 * Use the translation keys available in Phrase for the labels
 * - `designSystem.pagination.previousPageAriaLabel`
 * - `designSystem.pagination.nextPageAriaLabel`
 *
 * See the related [Figma](figma) for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15439-3557
 */
export const Pagination = ({
  count,
  currentPage,
  disabled = false,
  hrefForPage,
  levels,
  nextPageAriaLabel,
  onPageChange,
  previousPageAriaLabel,
}: PaginationProps) => {
  const pages = calculatePaginationPages(currentPage, count);
  const levelsToDisplay = levels?.length
    ? levels.filter((level) => level < count && level > currentPage + 2)
    : [];

  return (
    <nav role="navigation">
      <ol
        className="flex items-center justify-center gap-x-s rounded-card border
          border-foundation-primary bg-foundation-inverted p-s"
      >
        <PaginationItem
          ariaLabel={previousPageAriaLabel}
          disabled={disabled}
          onPageChange={onPageChange}
          hrefForPage={hrefForPage}
          count={count}
          current={false}
          page={currentPage - 1}
        >
          <ChevronLeftIcon className="size-[24px]" />
        </PaginationItem>

        {pages.map(({ isHiddenInMobile, page }, index) => {
          const nextPageItem = pages[index + 1] as
            | (typeof pages)[number]
            | undefined;
          return (
            <Fragment key={page}>
              {levelsToDisplay.length > 0 && page === count && (
                <li className="w-fit">
                  <ol
                    className="flex h-[32px] w-full flex-wrap justify-end
                      gap-x-s overflow-hidden"
                  >
                    {levelsToDisplay.map((level) => (
                      <PaginationPageItem
                        ariaLabel={`Page ${level}`}
                        key={level}
                        disabled={disabled}
                        onPageChange={onPageChange}
                        hrefForPage={hrefForPage}
                        count={count}
                        current={false}
                        page={level}
                      >
                        {level}
                      </PaginationPageItem>
                    ))}
                  </ol>
                </li>
              )}
              <PaginationPageItem
                ariaLabel={`Page ${page}`}
                disabled={disabled}
                onPageChange={onPageChange}
                current={page === currentPage}
                hrefForPage={hrefForPage}
                count={count}
                page={page}
                hiddenInMobile={isHiddenInMobile}
              >
                {page}
              </PaginationPageItem>
              {nextPageItem?.needsEllipsisInMobile && (
                <EllipsisItem showOnlyInMobile />
              )}
              {nextPageItem && nextPageItem?.page !== page + 1 && (
                <EllipsisItem />
              )}
            </Fragment>
          );
        })}

        <PaginationItem
          ariaLabel={nextPageAriaLabel}
          disabled={disabled}
          onPageChange={onPageChange}
          hrefForPage={hrefForPage}
          current={false}
          count={count}
          page={currentPage + 1}
        >
          <ChevronRightIcon className="size-[24px]" />
        </PaginationItem>
      </ol>
    </nav>
  );
};

Pagination.displayName = 'Pagination';
